import { useCallback, useMemo } from 'react';

import { DashboardAreaColorEnum, DashboardVisualizationTypeEnum, SchoolDashboardResponse, SheetTheme } from './types';
import { useDispatch, useSelector } from 'react-redux';
import {
  getDashboard,
  setDashboardAction,
  setDashboardAreaSearchAction,
  setDashboardForceOldDashboardAction,
  setDashboardUsingAction,
} from './redux/slice';
import { SchoolDashboardAreaSearchResponse } from './types/SchoolDashboardAreaSearchResponse';
import { darkenColor, lightenColor, request } from 'app/utils';
import { useTheme } from 'styled-components';
import { useSchool } from 'features/school';
import { useMenu } from 'features/menu';
import usingStorage from 'app/utils/storage';
import { useLocation } from 'react-router-dom';

const storage = usingStorage('localStorage');

const sheetThemesDefault: Record<DashboardVisualizationTypeEnum, Record<DashboardAreaColorEnum, SheetTheme>> = {
  [DashboardVisualizationTypeEnum.Standard]: {
    [DashboardAreaColorEnum.Light]: {
      sheetBg: 'white',

      titleColor: 'dark_350',

      buttonBg: 'white',
      buttonBgHover: 'gray_100',

      buttonColor: 'primary_400',
      buttonColorHover: 'primary_200',

      buttonBorderColor: 'gray_200',

      buttonDisabledBg: 'white',
      buttonDisabledColor: 'gray_200',
    },

    [DashboardAreaColorEnum.Dark]: {
      sheetBg: 'dark_350',

      titleColor: 'white',

      buttonBg: 'dark_150',
      buttonBgHover: 'dark_400',

      buttonColor: 'primary_400',
      buttonColorHover: 'primary_200',

      buttonBorderColor: 'dark_150',

      buttonDisabledBg: 'dark_150',
      buttonDisabledColor: 'gray_600',
    },

    [DashboardAreaColorEnum.Medium]: {
      sheetBg: 'gray_200',

      titleColor: 'dark_350',

      buttonBg: 'white',
      buttonBgHover: 'primary_400',

      buttonColor: 'primary_400',
      buttonColorHover: 'white',

      buttonBorderColor: 'gray_200',

      buttonDisabledBg: 'white',
      buttonDisabledColor: 'gray_200',
    },
  },

  [DashboardVisualizationTypeEnum.Cinema]: {
    [DashboardAreaColorEnum.Light]: {
      sheetBg: 'dark_400',
      titleColor: 'white',

      buttonBg: 'primary_400',
      buttonBgHover: 'navbarBg',

      buttonColor: 'navbarBg',
      buttonColorHover: 'white',

      buttonBorderColor: 'dark_400',

      buttonDisabledBg: 'dark_150',
      buttonDisabledColor: 'gray_500',
    },

    [DashboardAreaColorEnum.Dark]: {
      sheetBg: 'dark_350',
      titleColor: 'white',

      buttonBg: 'primary_400',
      buttonBgHover: 'navbarBg',

      buttonColor: 'navbarBg',
      buttonColorHover: 'white',

      buttonBorderColor: 'dark_350',

      buttonDisabledBg: 'dark_150',
      buttonDisabledColor: 'gray_500',
    },

    [DashboardAreaColorEnum.Medium]: {
      sheetBg: 'dark_350',
      titleColor: 'white',

      buttonBg: 'primary_400',
      buttonBgHover: 'navbarBg',

      buttonColor: 'navbarBg',
      buttonColorHover: 'white',

      buttonBorderColor: 'dark_350',

      buttonDisabledBg: 'dark_150',
      buttonDisabledColor: 'gray_500',
    },
  },
};

const useSheets = () => {
  const sheets = useSelector(getDashboard);

  const location = useLocation();

  const { setSchoolColors } = useTheme();
  const { schoolUuid, schoolAlias, apiGatewayUrl, setSchoolUiSettings } = useSchool();
  const { setCurrentDashboard } = useMenu();

  const dispatch = useDispatch();

  const setDashboard = (received: SchoolDashboardResponse) => {
    dispatch(setDashboardAction(received));
  };

  const clearDashboard = () => {
    dispatch(setDashboardAction(undefined!));
  };

  const setDashboardAreaSearch = (received: SchoolDashboardAreaSearchResponse) => {
    dispatch(setDashboardAreaSearchAction(received));
  };

  const setDashboardUsing = (usingDashboard: boolean | null) => {
    dispatch(setDashboardUsingAction(usingDashboard));
  };

  const setDashboardForceOld = (forceOldDashboard: boolean) => {
    dispatch(setDashboardForceOldDashboardAction(forceOldDashboard));
  };

  const clearDashboardAreaSearch = () => {
    dispatch(setDashboardAreaSearchAction(undefined!));
  };

  const sheetThemes = useMemo(() => {
    const customizedSheetThemes = { ...sheetThemesDefault };

    if (schoolAlias === 'b3') {
      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Light].buttonBg = 'gray_100';
      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Light].buttonBgHover = 'gray_200';
      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Light].buttonColor = 'dark_350';
      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Light].buttonColorHover = 'dark_350';
      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Light].buttonDisabledBg = 'gray_200';
      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Light].buttonDisabledColor = 'gray_400';
      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Light].buttonBorderColor = 'gray_300';

      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Medium].buttonBg = 'white';
      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Medium].buttonBgHover = 'gray_100';
      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Medium].buttonBorderColor = 'gray_100';
      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Medium].buttonColor = 'dark_350';
      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Medium].buttonColorHover = 'dark_350';
      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Medium].buttonDisabledBg = 'gray_200';
      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Medium].buttonDisabledColor = 'gray_400';

      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Dark].buttonBg = 'primary_400';
      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Dark].buttonBgHover = 'primary_600';
      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Dark].buttonBorderColor = 'dark_350';
      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Dark].buttonColor = 'dark_350';
      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Dark].buttonColorHover = 'dark_350';
      customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Dark].buttonDisabledBg = 'white80';
      // customizedSheetThemes[DashboardVisualizationTypeEnum.Standard][DashboardAreaColorEnum.Dark].buttonDisabledColor = 'dark_350_70';
    }

    return customizedSheetThemes;
  }, []);

  const sheetTheme = useCallback(
    (dashboardVisualizationType: DashboardVisualizationTypeEnum, dashboardAreaColor: DashboardAreaColorEnum): SheetTheme => {
      let sheetThemesColor = sheetThemes[dashboardVisualizationType][dashboardAreaColor];

      return sheetThemesColor;
    },
    []
  );

  const fetchDashboard = useCallback(
    async (dashboardUuid: string | null, cb?: (dashReturn: any) => void, withAuth = true) => {
      await request<SchoolDashboardResponse>({
        url: withAuth
          ? dashboardUuid
            ? `/api/v1/School/Dashboard/Get/${dashboardUuid}`
            : `/api/v1/School/Dashboard/Default`
          : dashboardUuid
          ? `/api/v1/School/Dashboard/ForSchool/${schoolUuid}/Get/${dashboardUuid}`
          : `/api/v1/School/Dashboard/ForSchool/${schoolUuid}/Default`,
        baseURL: apiGatewayUrl,
        method: 'get',
      })
        .then((response) => {
          const { linkButtonsColor, textIconColors, siteTopColor, logoDarkBackgroundImageUrl, logoLightBackgroundImageUrl, faviconImageUrl } =
            response.data;

          const PRIMARY_COLOR_400 = linkButtonsColor;
          const PRIMARY_COLOR_200 = lightenColor(PRIMARY_COLOR_400, 10);
          const PRIMARY_COLOR_600 = darkenColor(PRIMARY_COLOR_400, 10);

          setSchoolColors({
            primary_200: PRIMARY_COLOR_200,
            primary_400: PRIMARY_COLOR_400,
            primary_600: PRIMARY_COLOR_600,
            primary_contrast: textIconColors,
            navbarBg: siteTopColor,
          });

          setSchoolUiSettings({
            logoDarkUrl: logoDarkBackgroundImageUrl,
            logoUrl: logoLightBackgroundImageUrl,
            favIconUrl: faviconImageUrl,
          });

          let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");

          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.getElementsByTagName('head')[0].appendChild(link);
          }

          link.href = faviconImageUrl;

          setCurrentDashboard(response.data.uuid);
          setDashboard(response.data);
          setDashboardUsing(true);

          if (cb) cb({ dashboardUuid: response.data.uuid, hasDashboardUuid: !!dashboardUuid });
        })
        .catch(() => {
          setDashboardUsing(false);

          if (cb) cb({ dashboardUuid: null, hasDashboardUuid: !!dashboardUuid });
        });
    },
    [schoolUuid]
  );

  const getDashboardUi = useCallback(
    async (cb?: (dashUuid: string | null) => void, withAuth = false) => {
      if (sheets.usingDashboard || location.pathname === '/') return;

      const currentDashboardUuid = storage.get('currentDashboard') || null;

      await fetchDashboard(currentDashboardUuid, cb, withAuth);
    },
    [sheets, location]
  );

  return {
    ...sheets,
    setDashboard,
    clearDashboard,
    setDashboardAreaSearch,
    clearDashboardAreaSearch,
    setDashboardUsing,
    sheetTheme,
    fetchDashboard,
    getDashboardUi,
    setDashboardForceOld,
  };
};

export default useSheets;
